import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import type { Database } from '@/integrations/supabase/types';

export type Risk = Database['public']['Tables']['risks']['Row'];

interface RiskFilters {
  status?: string;
  riskLevel?: string;
}

interface CreateRiskData {
  title: string;
  asset_or_process?: string;
  cause?: string;
  consequence?: string;
  impact: number;
  likelihood: number;
  status?: string;
  notes?: string;
  treatment?: string;
  owner_id?: string;
  next_review?: string;
  related_to_type?: string;
  related_to_id?: string;
}

interface UpdateRiskData {
  title?: string;
  asset_or_process?: string;
  cause?: string;
  consequence?: string;
  impact?: number;
  likelihood?: number;
  status?: string;
  notes?: string;
  treatment?: string;
  owner_id?: string;
  next_review?: string;
  residual_score?: number;
}

export const useRisks = (orgId?: string, filters?: RiskFilters) => {
  const queryClient = useQueryClient();

  const risksQuery = useQuery({
    queryKey: ['risks', orgId, filters?.status, filters?.riskLevel],
    queryFn: async () => {
      if (!orgId) return [];
      
      let query = supabase
        .from('risks')
        .select('*')
        .eq('org_id', orgId);

      if (filters?.status && filters.status !== 'all') {
        query = query.eq('status', filters.status);
      }

      const { data, error } = await query.order('created_at', { ascending: false });

      if (error) throw error;
      
      let results = data || [];

      // Client-side risk level filtering
      if (filters?.riskLevel && filters.riskLevel !== 'all') {
        results = results.filter(risk => {
          const score = risk.risk_score || 0;
          if (filters.riskLevel === 'high' && score >= 15) return true;
          if (filters.riskLevel === 'medium' && score >= 10 && score < 15) return true;
          if (filters.riskLevel === 'low' && score >= 5 && score < 10) return true;
          if (filters.riskLevel === 'very-low' && score < 5) return true;
          return false;
        });
      }

      return results;
    },
    enabled: !!orgId,
  });

  const createRiskMutation = useMutation({
    mutationFn: async (data: CreateRiskData & { org_id: string }) => {
      console.log('Creating risk with data:', data);
      
      const insertData: any = {
        org_id: data.org_id,
        title: data.title,
        asset_or_process: data.asset_or_process,
        cause: data.cause,
        consequence: data.consequence,
        impact: data.impact,
        likelihood: data.likelihood,
        status: data.status || 'identified',
        notes: data.notes,
      };

      // Only include optional fields if they are provided
      if (data.treatment) insertData.treatment = data.treatment;
      if (data.owner_id) insertData.owner_id = data.owner_id;
      if (data.next_review) insertData.next_review = data.next_review;
      if (data.related_to_type) insertData.related_to_type = data.related_to_type;
      if (data.related_to_id) insertData.related_to_id = data.related_to_id;

      console.log('Insert data:', insertData);

      const { data: result, error } = await supabase
        .from('risks')
        .insert(insertData)
        .select()
        .single();

      if (error) {
        console.error('Supabase error:', error);
        throw error;
      }
      
      console.log('Risk created successfully:', result);
      return result;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['risks'] });
      toast.success('Risk created successfully');
    },
    onError: (error) => {
      console.error('Error creating risk:', error);
      toast.error('Failed to create risk');
    },
  });

  const updateRiskMutation = useMutation({
    mutationFn: async ({ id, data }: { id: string; data: UpdateRiskData }) => {
      const updates: any = { ...data };
      
      // Remove risk_score from updates since it's a generated column
      delete updates.risk_score;

      const { error } = await supabase
        .from('risks')
        .update(updates)
        .eq('id', id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['risks'] });
      toast.success('Risk updated successfully');
    },
    onError: (error) => {
      console.error('Error updating risk:', error);
      toast.error('Failed to update risk');
    },
  });

  const deleteRiskMutation = useMutation({
    mutationFn: async (id: string) => {
      // First delete related links
      await supabase.from('links').delete().or(`source_id.eq.${id},target_id.eq.${id}`);
      
      // Then delete the risk
      const { error } = await supabase.from('risks').delete().eq('id', id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['risks'] });
      queryClient.invalidateQueries({ queryKey: ['links'] });
      toast.success('Risk deleted successfully');
    },
    onError: (error) => {
      console.error('Error deleting risk:', error);
      toast.error('Failed to delete risk');
    },
  });

  const calculateRiskScore = (impact: number, likelihood: number) => {
    const score = impact * likelihood;
    let severity = 'Very Low';
    if (score >= 15) severity = 'High';
    else if (score >= 10) severity = 'Medium';
    else if (score >= 5) severity = 'Low';
    return { score, severity };
  };

  return {
    risks: risksQuery.data ?? [],
    isLoading: risksQuery.isLoading,
    error: risksQuery.error,
    createRisk: createRiskMutation.mutate,
    updateRisk: updateRiskMutation.mutate,
    deleteRisk: deleteRiskMutation.mutate,
    isCreating: createRiskMutation.isPending,
    isUpdating: updateRiskMutation.isPending,
    isDeleting: deleteRiskMutation.isPending,
    calculateRiskScore,
  };
};
